import { useTranslation } from 'react-i18next';
import { S, Icon, EmptyState, formatDate } from '../regureStyles';
import PermissionGate from '../../components/PermissionGate';

// ─── Status badge ─────────────────────────────────────────
const STATUS_CFG_STYLE = {
  ready:        { bg: '#dcfce7', color: '#15803d' },
  sent:         { bg: '#dbeafe', color: '#1d4ed8' },
  generating:   { bg: '#ede9fe', color: '#7c3aed' },
  acknowledged: { bg: '#f1f5f9', color: '#64748b' },
  error:        { bg: '#fee2e2', color: '#991b1b' },
};

const TYPE_LABEL_KEY = {
  claims:     'screens.bordereaux.typeClaims',
  premium:    'screens.bordereaux.typePremium',
  combined:   'screens.bordereaux.typeCombined',
  loss_run:   'screens.bordereaux.typeLossRun',
  commission: 'screens.bordereaux.typeCommission',
};

const StatusPill = ({ status, t }) => {
  const cfg = STATUS_CFG_STYLE[status] || STATUS_CFG_STYLE.generating;
  const labelKey = {
    ready: 'screens.bordereaux.statusReady',
    sent: 'screens.bordereaux.statusSent',
    generating: 'screens.bordereaux.statusGenerating',
    acknowledged: 'screens.bordereaux.statusAcknowledged',
    error: 'screens.bordereaux.statusError',
  }[status] || 'screens.bordereaux.statusGenerating';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: cfg.bg, color: cfg.color }}>
      {status === 'generating' && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', border: `2px solid ${cfg.color}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />}
      {t(labelKey)}
    </span>
  );
};

const Table = ({ loading, bordereaux, carriers, handleDownload, handleSend, handleDelete, sending, total, PAGE_SIZE, page, setPage, onRowClick }) => {
  const { t } = useTranslation();
  const headers = [
    t('screens.bordereaux.colCarrier'),
    t('screens.bordereaux.colType'),
    t('screens.bordereaux.colPeriod'),
    t('screens.bordereaux.colFormat'),
    t('screens.bordereaux.colRows'),
    t('screens.bordereaux.colFileSize'),
    t('screens.bordereaux.colGeneratedBy'),
    t('screens.bordereaux.colStatus'),
    t('screens.bordereaux.colActions'),
  ];
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px' }}>
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: S.textMuted }}>{t('screens.bordereaux.loading')}</div>
      ) : bordereaux.length === 0 ? (
        <EmptyState
          icon='file-text'
          title={t('screens.bordereaux.emptyTitle')}
          subtitle={carriers.length > 0 ? t('screens.bordereaux.emptySubtitleWithCarriers') : t('screens.bordereaux.emptySubtitleNoCarriers')}
        />
      ) : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headers.map(h => (
                  <th
                    key={h}
                    style={{
                      padding: '13px 14px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 700,
                      color: S.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      borderBottom: `2px solid ${S.borderLight}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bordereaux.map((b, i) => {
                const from = b.period?.from ? formatDate(b.period.from) : '—';
                const to = b.period?.to ? formatDate(b.period.to) : '—';
                const sizeKb = b.fileSize ? `${Math.round(b.fileSize / 1024)} KB` : '—';
                const canDownload = ['ready', 'sent', 'acknowledged'].includes(b.status);
                return (
                  <tr
                    key={b._id}
                    style={{ borderBottom: `1px solid ${S.borderLight}`, background: i % 2 === 0 ? '#fff' : S.bgCard, cursor: onRowClick ? 'pointer' : 'default' }}
                    onClick={(e) => {
                      // Skip when the click landed on an action button.
                      if (e.target.closest('button')) return;
                      if (onRowClick) onRowClick(b._id);
                    }}
                  >
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary }}>{b.carrier?.name || '—'}</div>
                      {b.carrier?.code && <div style={{ fontSize: 11, color: S.textMuted, fontFamily: 'monospace' }}>{b.carrier.code}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: S.textSecondary }}>{TYPE_LABEL_KEY[b.type] ? t(TYPE_LABEL_KEY[b.type]) : (b.type || '—')}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: S.textSecondary }}>
                      <div>{from}</div>
                      <div style={{ color: S.textMuted }}>→ {to}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, fontWeight: 700, color: S.textMuted, fontFamily: 'monospace' }}>
                      {(b.format || 'xlsx').toUpperCase()}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: S.textPrimary }}>{b.rowCount ?? '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: S.textMuted }}>{sizeKb}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: S.textMuted }}>{b.generatedBy?.name || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <StatusPill status={b.status} t={t} />
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {canDownload && (
                          <button
                            onClick={() => handleDownload(b._id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '5px 11px',
                              background: S.bgCard,
                              border: `1px solid ${S.borderLight}`,
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              color: S.textSecondary,
                              cursor: 'pointer',
                            }}
                          >
                            <Icon name='download' color={S.textMuted} size={12} /> {t('screens.bordereaux.actionDownload')}
                          </button>
                        )}
                        {b.status === 'ready' && (
                          <PermissionGate resource='bordereaux' action='send'>
                            <button
                              onClick={() => handleSend(b._id)}
                              disabled={sending === b._id}
                              style={{
                                padding: '5px 11px',
                                background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
                                color: '#fff',
                                border: 'none',
                                borderRadius: 6,
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                opacity: sending === b._id ? 0.6 : 1,
                              }}
                            >
                              {sending === b._id ? '…' : t('screens.bordereaux.actionSend')}
                            </button>
                          </PermissionGate>
                        )}
                        {b.status === 'error' && b.errorMessage && (
                          <span title={b.errorMessage} style={{ fontSize: 12, color: S.danger, cursor: 'help' }}>
                            ⚠ {t('screens.bordereaux.errorLabel')}
                          </span>
                        )}
                        <PermissionGate resource='bordereaux' action='delete'>
                          <button
                            onClick={() => handleDelete && handleDelete(b._id)}
                            title={t('common.delete', { defaultValue: 'Delete' })}
                            style={{
                              padding: '5px 9px',
                              background: '#fff',
                              border: `1px solid ${S.borderLight}`,
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              color: '#991b1b',
                              cursor: 'pointer',
                            }}
                          >
                            <Icon name='trash' color='#991b1b' size={12} /> {t('common.delete', { defaultValue: 'Delete' })}
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Pagination ────────────────────────────── */}
          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '20px 0' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '7px 14px',
                  background: S.bgCard,
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  color: page === 1 ? S.textMuted : S.textPrimary,
                  cursor: page === 1 ? 'default' : 'pointer',
                }}
              >
                {t('screens.bordereaux.previous')}
              </button>
              <span style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: S.textMuted, padding: '0 8px' }}>
                {t('screens.bordereaux.pageOf', { page, total: Math.ceil(total / PAGE_SIZE) })}
              </span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / PAGE_SIZE)}
                style={{
                  padding: '7px 14px',
                  background: S.bgCard,
                  border: `1px solid ${S.borderLight}`,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  color: page >= Math.ceil(total / PAGE_SIZE) ? S.textMuted : S.textPrimary,
                  cursor: page >= Math.ceil(total / PAGE_SIZE) ? 'default' : 'pointer',
                }}
              >
                {t('screens.bordereaux.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Table;
